import { useEffect, useMemo, useState } from 'react'
import { useFormContext } from 'react-hook-form'

import { Center, Checkbox, Divider, Group, SimpleGrid, Stack, Title } from '@mantine/core'
import { BooleanInput, useRecordContext, useTranslate } from 'react-admin'
import {
  BeneficiaryApiDTO,
  BeneficiaryStatementSettingsApiDTO,
} from '../../../codegen/generated/Api.schemas'
import { useGetApiStatementsCsvExportColumns } from '../../../codegen/generated/statements'
import { LXLoader } from '../../../components/LXLoader'
import { BeneficiaryHelper } from '../../../helpers/Beneficiary.helper'

export const BeneficiaryEditStatementSettings: React.FC = () => {
  const t = useTranslate()
  const record = useRecordContext<BeneficiaryApiDTO>()
  const { setValue } = useFormContext()

  const [isDataSet, setIsDataSet] = useState(false)
  const [statementColumns, setStatementColumns] = useState<string[]>()
  const [basicStatementColumns, setBasicStatementColumns] = useState<string[]>()
  const [artworkChannelStatementColumns, setArtworkChannelStatementColumns] = useState<string[]>()

  const statementsCsvExportColumnsQuery = useGetApiStatementsCsvExportColumns()

  const defaultColumns = useMemo(
    () => ({
      statement: Object.entries(record.statementSettings.csvColumns?.statement ?? [])
        .map(c => (c[1] ? c[0] : ''))
        .filter(c => c !== ''),

      basicStatement: Object.entries(record.statementSettings.csvColumns?.basicStatement ?? [])
        .map(c => (c[1] ? c[0] : ''))
        .filter(c => c !== ''),

      artworkChannelStatement: Object.entries(
        record.statementSettings.csvColumns?.artworkChannelStatement ?? [],
      )
        .map(c => (c[1] ? c[0] : ''))
        .filter(c => c !== ''),
    }),
    [record.statementSettings.csvColumns],
  )

  useEffect(() => {
    if (!isDataSet) {
      setStatementColumns(defaultColumns.statement)
      setBasicStatementColumns(defaultColumns.basicStatement)
      setArtworkChannelStatementColumns(defaultColumns.artworkChannelStatement)

      setIsDataSet(true)
    }
  }, [defaultColumns, isDataSet])

  useEffect(() => {
    if (!isDataSet) {
      return
    }
    const statement = BeneficiaryHelper.arrayToSettingsColumns(
      statementColumns ?? [],
      statementsCsvExportColumnsQuery.data?.body.statement.map(c => c.key) ?? [],
    )

    const basicStatement = BeneficiaryHelper.arrayToSettingsColumns(
      basicStatementColumns ?? [],
      statementsCsvExportColumnsQuery.data?.body.basicStatement.map(c => c.key) ?? [],
    )

    const artworkChannelStatement = BeneficiaryHelper.arrayToSettingsColumns(
      artworkChannelStatementColumns ?? [],
      statementsCsvExportColumnsQuery.data?.body.artworkChannelStatement.map(c => c.key) ?? [],
    )
    setValue(
      'statementSettings',
      {
        ...record.statementSettings,
        csvColumns: {
          statement,
          basicStatement,
          artworkChannelStatement,
        },
      } satisfies BeneficiaryStatementSettingsApiDTO,
      { shouldDirty: true },
    )
  }, [
    record.statementSettings,
    setValue,
    statementColumns,
    basicStatementColumns,
    artworkChannelStatementColumns,
    statementsCsvExportColumnsQuery.data?.body,
  ])

  if (statementsCsvExportColumnsQuery.isLoading) {
    return (
      <Center w="100%">
        <LXLoader />
      </Center>
    )
  }

  return (
    <Stack>
      <Title order={5} mt="xs">
        {t('_generics.sections')}
      </Title>
      <Divider />
      <SimpleGrid cols={3} mb="md">
        <BooleanInput
          source="statementSettings.introduction"
          label={t('pages.beneficiaries.statementSettings.introduction')}
        />
        <BooleanInput
          source="statementSettings.globalInformations"
          label={t('pages.beneficiaries.statementSettings.globalInformation')}
        />
        <BooleanInput
          validate={(value, values) => {
            if (values.statementSettings.globalInformations && value) {
              return t('pages.beneficiaries.statementSettings.firstPageLXGreetingsError')
            }

            return null
          }}
          source="statementSettings.firstPageLXGreetings"
          label={t('pages.beneficiaries.statementSettings.firstPageLXGreetings')}
        />
      </SimpleGrid>
      <Title order={5} mt="xs">
        {t('_generics.performances')}
      </Title>
      <Divider />
      <SimpleGrid cols={3} mb="md">
        <BooleanInput
          source="statementSettings.productTypes"
          label={t('pages.beneficiaries.statementSettings.productTypes')}
        />
        <BooleanInput
          source="statementSettings.products"
          label={t('pages.beneficiaries.statementSettings.products')}
        />
        <BooleanInput
          source="statementSettings.artworks"
          label={t('pages.beneficiaries.statementSettings.artworks')}
        />
      </SimpleGrid>

      <Title order={5} mt="xs">
        {t('_generics.revenues')}
      </Title>
      <Divider />
      <SimpleGrid cols={3} mb="md">
        <BooleanInput
          source="statementSettings.shorts"
          label={t('pages.beneficiaries.statementSettings.shorts')}
        />
        <BooleanInput
          source="statementSettings.adSubscriptionBreakdown"
          label={t('pages.beneficiaries.statementSettings.adSubscriptionBreakdown')}
        />
      </SimpleGrid>

      <Title order={5} mt="xs">
        {t('_generics.pdfs')}
      </Title>
      <Divider />
      <SimpleGrid cols={3} mb="md">
        <BooleanInput
          source="statementSettings.monthlyComparison"
          label={t('pages.beneficiaries.statementSettings.monthlyComparison')}
        />
        <BooleanInput
          source="statementSettings.yearlyComparison"
          label={t('pages.beneficiaries.statementSettings.yearlyComparison')}
        />
      </SimpleGrid>

      <Title order={5} mt="xs">
        {t('_generics.csvs')}
      </Title>
      <Divider />
      <Stack gap="xl">
        <Checkbox.Group
          label={<Title order={6}>{t('pages.beneficiaries.statementNames.statement')}</Title>}
          defaultValue={defaultColumns.statement}
          value={statementColumns}
          onChange={setStatementColumns}
        >
          <Group mt="xs">
            {statementsCsvExportColumnsQuery.data?.body.statement.map(c => (
              <Checkbox
                value={c.key}
                label={c.column}
                key={c.key}
                styles={{
                  label: {
                    opacity: statementColumns?.includes(c.key) ? 1 : 0.6,
                  },
                }}
              />
            ))}
          </Group>
        </Checkbox.Group>
        <Checkbox.Group
          label={<Title order={6}>{t('pages.beneficiaries.statementNames.basicStatement')}</Title>}
          defaultValue={defaultColumns.basicStatement}
          value={basicStatementColumns}
          onChange={setBasicStatementColumns}
        >
          <Group mt="xs">
            {statementsCsvExportColumnsQuery.data?.body.basicStatement.map(c => (
              <Checkbox
                value={c.key}
                label={c.column}
                key={c.key}
                styles={{
                  label: {
                    opacity: basicStatementColumns?.includes(c.key) ? 1 : 0.6,
                  },
                }}
              />
            ))}
          </Group>
        </Checkbox.Group>
        <Checkbox.Group
          label={
            <Title order={6}>
              {t('pages.beneficiaries.statementNames.artworkChannelStatement')}
            </Title>
          }
          defaultValue={defaultColumns.artworkChannelStatement}
          value={artworkChannelStatementColumns}
          onChange={setArtworkChannelStatementColumns}
        >
          <Group mt="xs">
            {statementsCsvExportColumnsQuery.data?.body.artworkChannelStatement.map(c => (
              <Checkbox
                value={c.key}
                label={c.column}
                key={c.key}
                styles={{
                  label: {
                    opacity: artworkChannelStatementColumns?.includes(c.key) ? 1 : 0.6,
                  },
                }}
              />
            ))}
          </Group>
        </Checkbox.Group>
        {/*
        <Stack gap="xs">
          <Title order={6}>{t('pages.beneficiaries.statementNames.artworkChannelStatement')}</Title>
          <CheckboxGroupInput
            label=""
            source="statementSettings.artworkChannelStatement"
            choices={exportColumns.artworkChannelStatement}
            optionText="label"
          />
        </Stack>
      */}
        {/*
        <Stack gap="xs">
          <Title order={6}>{t('pages.beneficiaries.statementNames.artworkChannelStatement')}</Title>
           <CheckboxGroupInput
            label=""
            source="statementSettings.csvColumns.artworkChannelStatement"
            choices={statementsCsvExportColumnsQuery.data?.body.artworkChannelStatement ?? []}
            optionValue="key"
            optionText="column"
          />
        </Stack>
      */}
      </Stack>
    </Stack>
  )
}
